import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, MapPin, Users, Search } from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { useEnquiry } from "../components/EnquiryModal";
import { useLanguage } from "../i18n/LanguageContext";
import { PRODUCTS } from "../data/content";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, isAr } = useLanguage();
  const { open } = useEnquiry();

  const index = PRODUCTS.findIndex((p) => p.id === id);
  if (index === -1) return <Navigate to="/products" replace />;

  const product = PRODUCTS[index];
  const info = t(`products.${product.key}`);
  const prev = PRODUCTS[(index - 1 + PRODUCTS.length) % PRODUCTS.length];
  const next = PRODUCTS[(index + 1) % PRODUCTS.length];
  const others = PRODUCTS.filter((p) => p.id !== id).slice(0, 3);
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  return (
    <main>
      <PageMeta
        title={`${info.name} | Al Nama Importer – Food Trading Company Dubai`}
        description={info.desc}
        titleAr={`${info.name} | النعم للاستيراد – شركة تجارة غذائية دبي`}
        descriptionAr={info.desc}
      />
      <PageHero
        image={product.img}
        overline={`${t("productsPage.heroOver")} · ${product.tag}`}
        titleLines={[info.name]}
        text={info.desc}
      />

      <section className="bg-[#09121F] py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <Reveal>
              <Link
                to="/products"
                data-testid="product-detail-back-link"
                className="group inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:text-amber-300"
              >
                <BackIcon className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1 rtl:group-hover:translate-x-1" />
                {t("detail.back")}
              </Link>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-8 text-base font-light leading-relaxed text-slate-300/90 sm:text-lg">
                {info.intro}
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 rounded-lg border border-amber-500/20 bg-[#0B1D16]/60 p-5 text-sm font-light leading-relaxed text-slate-400">
                {t("detail.availNote")}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <button
                data-testid="product-detail-enquiry-btn"
                onClick={() => open({ product: product.nameEn, type: "Product Enquiry" })}
                className="group mt-10 inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0B1D16] transition-transform duration-300 hover:scale-105"
              >
                {t("detail.enquire")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </Reveal>
          </div>

          <div className="space-y-6">
            <Reveal delay={0.1}>
              <div className="rounded-lg border border-amber-500/20 bg-[#0F1E18]/60 p-7">
                <p className="text-xs uppercase tracking-[0.25em] font-display font-semibold text-amber-400">
                  {t("detail.includes")}
                </p>
                <ul className="mt-5 flex flex-wrap gap-2.5">
                  {info.items.map((item) => (
                    <li
                      key={item}
                      className="rounded-full border border-amber-500/25 bg-[#0B1D16]/70 px-4 py-2 text-sm text-emerald-50"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="rounded-lg border border-amber-500/20 bg-[#0F1E18]/60 p-7">
                <div className="flex items-center gap-3">
                  <Search className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
                  <p className="font-display font-medium text-emerald-50">{t("detail.sourcingT")}</p>
                </div>
                <p className="mt-3 text-sm font-light leading-relaxed text-slate-300/85">
                  {t("detail.sourcingX")}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="rounded-lg border border-amber-500/20 bg-[#0F1E18]/60 p-7">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
                  <p className="font-display font-medium text-emerald-50">{t("detail.buyersT")}</p>
                </div>
                <p className="mt-3 text-sm font-light leading-relaxed text-slate-300/85">
                  {t("detail.buyersX")}
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mx-auto mt-20 flex max-w-7xl items-center justify-between border-t border-amber-500/10 px-5 pt-10 sm:px-8">
          <Link
            to={`/products/${prev.id}`}
            data-testid="product-detail-prev-link"
            className="group flex items-center gap-3 text-start"
          >
            <ArrowLeft className="h-4 w-4 text-amber-400 rtl:rotate-180" />
            <span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("detail.prev")}</span>
              <span className="block font-serif text-lg text-emerald-50 transition-colors group-hover:text-amber-400">
                {t(`products.${prev.key}.name`)}
              </span>
            </span>
          </Link>
          <Link
            to={`/products/${next.id}`}
            data-testid="product-detail-next-link"
            className="group flex items-center gap-3 text-end"
          >
            <span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("detail.next")}</span>
              <span className="block font-serif text-lg text-emerald-50 transition-colors group-hover:text-amber-400">
                {t(`products.${next.key}.name`)}
              </span>
            </span>
            <ArrowRight className="h-4 w-4 text-amber-400 rtl:rotate-180" />
          </Link>
        </div>
      </section>

      <section className="bg-[#0B1D16] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-4">
              <MapPin className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl font-semibold text-emerald-50 sm:text-3xl">
                {t("homeProducts.overline")}
              </h2>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <ProductCard product={p} index={i + 3} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
